import Vue, {reactive} from 'vue'
import Swal from 'sweetalert2'
import {useAuthService} from '@/core/api/auth'
import {useToken} from '@/core/utils/token'
import {User, UserPayload} from '@/core/models/user'

const tokenHelper = useToken()

const ONE_HOUR_IN_MS = 60 * 60 * 1000
const ONE_DAY_IN_MS = 24 * ONE_HOUR_IN_MS
const EXPIRY_WARNING_THRESHOLDS_MS = [ONE_HOUR_IN_MS, ONE_DAY_IN_MS]

const state = reactive({
    user: new User(<UserPayload>{}),
})

export const useAuth = () => {

    const auth = <T>(): Promise<T> => {
        return new Promise<T>((resolve, reject) => {
            if (tokenHelper.getToken()) {
                const authService = useAuthService()
                authService.getUser()
                    .then((data: any) => {
                        state.user.fill(<UserPayload>data)

                        if (data.expires_at) {
                            tokenHelper.setExpiresAt(data.expires_at)
                        }

                        resolve(data as T)
                    })
                    .catch((response: any) => {
                        reject(response as T)
                    })
            } else {
                resolve({} as T)
            }
        })
    }

    const user = (): User => {
        return state.user
    }

    const signed = (): boolean => {
        return !!state.user.id
    }

    const watchToken = () => {
        setInterval(() => {
            if (signed() && !tokenHelper.getToken()) {
                window.location.reload()
                return
            }

            warnIfTokenExpiresSoon()
        }, 10000)
    }

    const warnIfTokenExpiresSoon = () => {
        const expiresAt = tokenHelper.getExpiresAt()

        if (!signed() || !expiresAt) return

        const msRemaining = new Date(expiresAt).getTime() - Date.now()
        if (msRemaining <= 0) return

        const warnedThresholds = tokenHelper.getWarnedExpiryThresholds()
        const threshold = EXPIRY_WARNING_THRESHOLDS_MS.find((ms) => msRemaining <= ms && !warnedThresholds.includes(ms))
        if (!threshold) return

        tokenHelper.addWarnedExpiryThreshold(threshold)

        const hoursRemaining = Math.ceil(threshold / ONE_HOUR_IN_MS)
        const hoursLabel = hoursRemaining === 1 ? 'einer Stunde' : `${hoursRemaining} Stunden`

        Swal.fire({
            title: 'Anmeldung läuft bald ab',
            text: `Ihre Anmeldung läuft in weniger als ${hoursLabel} ab. Bitte melden Sie sich einmal ab und wieder an, damit Sie angemeldet bleiben.`,
            icon: 'info',
            confirmButtonText: 'Verstanden',
        })
    }

    const logout = () => {
        const authService = useAuthService()

        authService.logout()

        state.user.fill(<UserPayload>{})
    }

    return {
        auth,
        user,
        signed,
        watchToken,
        logout
    }
}

export const auth = {
    install: (VueInstance: typeof Vue, options: any) => {
        const auth = useAuth()

        VueInstance.prototype.$auth = auth
        VueInstance.prototype.permitted = (permission: string): boolean => {
            return auth.user().permitted(permission)
        }
        VueInstance.prototype.notPermitted = (permission: string): boolean => {
            return auth.user().notPermitted(permission)
        }
    }
}

Vue.use(auth)
